import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { apiCopyAssignments, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CalendarDays, AlertCircle, ChevronRight } from "lucide-react";

// -- date helpers (kept local to avoid importing Roster's ones)
function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function mondayOf(d) {
    const dt = new Date(d);
    const wd = (dt.getDay() + 6) % 7; // Mon=0 .. Sun=6
    dt.setDate(dt.getDate() - wd);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function addDays(d, n) {
    const dt = new Date(d);
    dt.setDate(dt.getDate() + n);
    return dt;
}
function formatWeekRange(monday) {
    const sun = addDays(monday, 6);
    const opt = { month: "short", day: "numeric" };
    const y = monday.getFullYear() !== sun.getFullYear() ? `, ${sun.getFullYear()}` : `, ${monday.getFullYear()}`;
    return `${monday.toLocaleDateString("en-US", opt)} – ${sun.toLocaleDateString("en-US", opt)}${y}`;
}

/**
 * Bulk-copy roster assignments dialog.
 * Props:
 *  - open, onOpenChange
 *  - project: current project object
 *  - sourceWeekStart, sourceWeekEnd: Date — the currently-viewed week bounds
 *  - shifts: [{shift_id, name, start_time, end_time}]
 *  - sourceAssignments: assignments already loaded for the source range (for count preview)
 *  - onSuccess: callback after copy completes → parent refreshes roster
 */
export default function CopyShiftsDialog({
    open,
    onOpenChange,
    project,
    sourceWeekStart,
    sourceWeekEnd,
    shifts = [],
    sourceAssignments = [],
    onSuccess,
}) {
    const [selectedShiftIds, setSelectedShiftIds] = useState([]);
    const [selectedWeeks, setSelectedWeeks] = useState([]); // array of ISO Monday strings
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);

    // 12 upcoming week candidates starting from the week AFTER the source week
    const targetCandidates = useMemo(() => {
        if (!sourceWeekStart) return [];
        const startMon = mondayOf(sourceWeekStart);
        const arr = [];
        for (let i = 1; i <= 12; i++) {
            const m = addDays(startMon, i * 7);
            arr.push({ iso: toISO(m), label: formatWeekRange(m), date: m });
        }
        return arr;
    }, [sourceWeekStart]);

    // Reset when opened
    useEffect(() => {
        if (open) {
            setSelectedShiftIds(shifts.map((s) => s.shift_id));
            setSelectedWeeks([]);
            setResult(null);
        }
    }, [open, shifts]);

    // Preview: how many source assignments will map into each week
    const sourceCount = useMemo(() => {
        if (!selectedShiftIds.length) return 0;
        return sourceAssignments.filter((a) => selectedShiftIds.includes(a.shift_id)).length;
    }, [selectedShiftIds, sourceAssignments]);

    const previewCreations = sourceCount * selectedWeeks.length;

    function toggleShift(id) {
        setSelectedShiftIds((cur) =>
            cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
        );
    }
    function toggleWeek(iso) {
        setSelectedWeeks((cur) =>
            cur.includes(iso) ? cur.filter((x) => x !== iso) : [...cur, iso],
        );
    }
    function selectAllShifts(v) {
        setSelectedShiftIds(v ? shifts.map((s) => s.shift_id) : []);
    }
    function selectAllWeeks(v) {
        setSelectedWeeks(v ? targetCandidates.map((w) => w.iso) : []);
    }

    async function apply() {
        if (!selectedWeeks.length) {
            toast.error("Pick at least one target week");
            return;
        }
        if (!sourceCount) {
            toast.error("No source shifts selected");
            return;
        }
        setSaving(true);
        try {
            const res = await apiCopyAssignments({
                project_id: project.project_id,
                source_from_date: toISO(sourceWeekStart),
                source_to_date: toISO(sourceWeekEnd),
                shift_ids: selectedShiftIds.length === shifts.length ? null : selectedShiftIds,
                target_week_starts: selectedWeeks,
            });
            setResult(res);
            const total = res.created_count || 0;
            const skip = res.skipped_count || 0;
            toast.success(
                `Copied ${total} assignment${total === 1 ? "" : "s"}` +
                    (skip ? ` · ${skip} skipped` : ""),
            );
            onSuccess?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
                data-testid="copy-shifts-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="h-5 w-5 text-[#0072BC]" />
                        Copy shift pattern
                    </DialogTitle>
                    <DialogDescription>
                        Copy the currently-viewed week's assignments to one or more upcoming weeks.
                        Duplicates and approved-leave conflicts will be skipped automatically.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <div className="space-y-4">
                        {/* Source summary */}
                        <div className="rounded-md border border-ntt-border bg-[#F4F6F9] p-3">
                            <div className="text-[11px] uppercase tracking-wider text-ntt-muted mb-1">
                                Source
                            </div>
                            <div className="flex items-center gap-2 text-[13px] font-semibold text-[#070F26]">
                                <CalendarDays className="h-4 w-4 text-[#0072BC]" />
                                {sourceWeekStart ? formatWeekRange(mondayOf(sourceWeekStart)) : "—"}
                            </div>
                            <div className="text-[12px] text-ntt-muted mt-1">
                                Project: <span className="text-[#070F26]">{project?.name}</span>
                            </div>
                        </div>

                        {/* Shift filter */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-[12px] font-medium text-[#070F26]">
                                    Shifts to include ({selectedShiftIds.length}/{shifts.length})
                                </Label>
                                <div className="flex items-center gap-2 text-[11px] text-ntt-muted">
                                    <button
                                        type="button"
                                        onClick={() => selectAllShifts(true)}
                                        data-testid="copy-shifts-select-all-shifts"
                                        className="hover:text-[#0072BC]"
                                    >
                                        Select all
                                    </button>
                                    <span>·</span>
                                    <button
                                        type="button"
                                        onClick={() => selectAllShifts(false)}
                                        data-testid="copy-shifts-clear-shifts"
                                        className="hover:text-[#0072BC]"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2" data-testid="copy-shifts-shift-list">
                                {shifts.length === 0 && (
                                    <div className="col-span-2 text-[12px] text-ntt-muted italic p-2">
                                        No shifts on this project.
                                    </div>
                                )}
                                {shifts.map((s) => (
                                    <label
                                        key={s.shift_id}
                                        data-testid={`copy-shifts-shift-${s.shift_id}`}
                                        className="flex items-center gap-2 rounded-md border border-ntt-border px-2 py-1.5 text-[12px] cursor-pointer hover:bg-[#FBFCFE]"
                                    >
                                        <Checkbox
                                            checked={selectedShiftIds.includes(s.shift_id)}
                                            onCheckedChange={() => toggleShift(s.shift_id)}
                                        />
                                        <div className="min-w-0">
                                            <div className="font-medium text-[#070F26] truncate">
                                                {s.name}
                                            </div>
                                            <div className="text-[11px] text-ntt-muted font-mono">
                                                {s.start_time}–{s.end_time}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Target weeks */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-[12px] font-medium text-[#070F26]">
                                    Target week(s) — up to 12 weeks ahead ({selectedWeeks.length} selected)
                                </Label>
                                <div className="flex items-center gap-2 text-[11px] text-ntt-muted">
                                    <button
                                        type="button"
                                        onClick={() => selectAllWeeks(true)}
                                        data-testid="copy-shifts-select-all-weeks"
                                        className="hover:text-[#0072BC]"
                                    >
                                        Select all
                                    </button>
                                    <span>·</span>
                                    <button
                                        type="button"
                                        onClick={() => selectAllWeeks(false)}
                                        data-testid="copy-shifts-clear-weeks"
                                        className="hover:text-[#0072BC]"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div
                                className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto rounded-md border border-ntt-border p-1.5"
                                data-testid="copy-shifts-week-list"
                            >
                                {targetCandidates.map((w) => (
                                    <label
                                        key={w.iso}
                                        data-testid={`copy-shifts-week-${w.iso}`}
                                        className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-[12px] cursor-pointer transition-colors ${
                                            selectedWeeks.includes(w.iso)
                                                ? "border-[#0072BC] bg-[#DBEAF6]/40"
                                                : "border-ntt-border hover:bg-[#FBFCFE]"
                                        }`}
                                    >
                                        <Checkbox
                                            checked={selectedWeeks.includes(w.iso)}
                                            onCheckedChange={() => toggleWeek(w.iso)}
                                        />
                                        <span className="truncate">{w.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Preview */}
                        <div
                            data-testid="copy-shifts-preview"
                            className="rounded-md border border-[#0072BC]/30 bg-[#0072BC]/5 p-3 text-[12px] text-[#070F26]"
                        >
                            <div className="flex items-center gap-1.5 font-medium mb-1">
                                <ChevronRight className="h-3.5 w-3.5 text-[#0072BC]" />
                                Preview
                            </div>
                            <div>
                                Will attempt to create <b>{previewCreations}</b> assignment{previewCreations === 1 ? "" : "s"}
                                {" "}({sourceCount} source × {selectedWeeks.length} week
                                {selectedWeeks.length === 1 ? "" : "s"}).
                            </div>
                            <div className="text-[11px] text-ntt-muted mt-1">
                                Duplicates and approved-leave conflicts on the target date will be skipped.
                            </div>
                        </div>
                    </div>
                ) : (
                    <ResultSummary result={result} />
                )}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="copy-shifts-close"
                    >
                        {result ? "Close" : "Cancel"}
                    </Button>
                    {!result && (
                        <Button
                            onClick={apply}
                            disabled={saving || previewCreations === 0}
                            data-testid="copy-shifts-apply"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Copying…" : `Copy to ${selectedWeeks.length} week${selectedWeeks.length === 1 ? "" : "s"}`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ResultSummary({ result }) {
    const skipReasons = {};
    for (const s of result.skipped || []) {
        skipReasons[s.reason] = (skipReasons[s.reason] || 0) + 1;
    }
    const REASON_LABEL = {
        duplicate: "Already existed",
        user_on_approved_leave: "User on approved leave",
    };
    return (
        <div className="space-y-3" data-testid="copy-shifts-result">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-800">
                <b>{result.created_count}</b> assignment
                {result.created_count === 1 ? "" : "s"} created across{" "}
                <b>{result.target_weeks}</b> week{result.target_weeks === 1 ? "" : "s"}.
            </div>
            {result.skipped_count > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800">
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {result.skipped_count} row(s) skipped
                    </div>
                    <ul className="ml-4 list-disc space-y-0.5">
                        {Object.entries(skipReasons).map(([k, v]) => (
                            <li key={k}>
                                <b>{v}</b> — {REASON_LABEL[k] || k}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
